"use client"

import {
  Award,
  Clock,
  Gift,
  ImageIcon,
  LinkIcon,
  MapPin,
  Tag,
  Trash2,
  Users,
} from "lucide-react"
import type { FormEvent } from "react"
import { useState } from "react"

import { RichTextEditor } from "@/components/rich-text-editor"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

export interface Neighborhood {
  id: number
  name: string
  city?: { id: number; name: string } | null
}
export interface Business {
  id: number
  business_name: string
}

export interface ViralCampaignFormData {
  base_entries?: number | ""
  entries_per_referral?: number | ""
  max_entries_per_user?: number | ""
  status?: "draft" | "active" | "ended" | "drawn" | ""
}

export interface RaffleFormData {
  title: string
  description: string
  prize_description: string
  prize_value: string
  start_date: string
  end_date: string
  city_id: string | number
  neighborhood_id: string
  max_participants: string
  winner_count: string
  sponsor_id: string
  terms_and_conditions: string
  requirements: {
    min_points?: string
    min_events_attended?: string
    min_forum_posts?: string
    required_neighborhood?: string
  }
  image?: File | null
  image_url?: string
  remove_image?: boolean
  create_viral_campaign?: boolean
  viral_campaign?: ViralCampaignFormData
}

export interface RaffleFormProps {
  title: string
  data: RaffleFormData
  errors: Record<string, string>
  neighborhoods: Neighborhood[]
  businesses: Business[]
  processing?: boolean
  onChange: (partial: Partial<RaffleFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
  currentImage?: string | null
}

export function RaffleForm({
  title,
  data,
  errors,
  neighborhoods,
  businesses,
  processing = false,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
  currentImage = null,
}: RaffleFormProps) {
  const err = errors

  const [imageMode, setImageMode] = useState<"file" | "url">("file")

  const toDatePart = (value: string) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  const toTimePart = (value: string) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const combineDateTime = (date: string, time: string) => {
    if (!date) return ""
    return time ? `${date}T${time}` : `${date}T00:00`
  }

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <Gift className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-8">
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Título del Sorteo
              </Label>
              <Input
                value={data.title}
                onChange={(e) => onChange({ title: e.target.value })}
                required
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Ej: Sorteo de cena para dos personas"
              />
              {err.title && (
                <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                  <div className="bg-status-error h-1 w-1 rounded-full"></div>
                  {err.title}
                </p>
              )}
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Descripción
              </Label>
              <Textarea
                value={data.description}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={4}
                className="border-border focus:border-primary resize-none border-2 text-base transition-colors duration-200"
                placeholder="Describe los detalles del sorteo..."
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Imagen del Sorteo
              </h3>
            </div>

            {currentImage && !data.remove_image && (
              <div className="bg-muted/50 border-muted flex items-center gap-4 rounded-lg border-2 border-dashed p-4">
                <div className="border-border relative h-24 w-40 overflow-hidden rounded-lg border-2">
                  <img
                    src={currentImage || "/placeholder.svg"}
                    alt="Imagen actual"
                    className="h-full w-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    onChange({ remove_image: true, image: null, image_url: "" })
                  }
                  className="h-10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={imageMode === "file" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("file")}
                className="h-8 shrink-0 px-4"
              >
                <ImageIcon className="mr-2 h-4 w-4" />
                Subir archivo
              </Button>
              <Button
                type="button"
                variant={imageMode === "url" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("url")}
                className="h-8 shrink-0 px-4"
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Usar URL
              </Button>
            </div>

            {imageMode === "file" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    onChange({ image: file })
                  }}
                  className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 w-full border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
                />
              </div>
            )}

            {imageMode === "url" && (
              <div className="w-full max-w-full min-w-0">
                <Input
                  type="url"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  value={data.image_url ?? ""}
                  onChange={(e) => onChange({ image_url: e.target.value })}
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Award className="text-secondary h-5 w-5" />
                Premio y Patrocinador
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Patrocinador
                </Label>
                <Select
                  value={data.sponsor_id}
                  onValueChange={(value) => onChange({ sponsor_id: value })}
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona un negocio" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {err.sponsor_id && (
                  <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                    <div className="bg-status-error h-1 w-1 rounded-full"></div>
                    {err.sponsor_id}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Valor del premio (€)
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={data.prize_value}
                  onChange={(e) => onChange({ prize_value: e.target.value })}
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Descripción del premio
              </Label>
              <Textarea
                value={data.prize_description}
                onChange={(e) =>
                  onChange({ prize_description: e.target.value })
                }
                rows={3}
                className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                placeholder="Describe el premio en detalle..."
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Clock className="text-primary h-5 w-5" />
                Fechas del Sorteo
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <Label className="text-muted-foreground text-sm font-medium">
                  Fecha y hora de inicio
                </Label>
                <div className="space-y-3">
                  <Input
                    type="date"
                    value={toDatePart(data.start_date)}
                    onChange={(e) => {
                      const newDate = e.target.value
                      const time = toTimePart(data.start_date)
                      onChange({ start_date: combineDateTime(newDate, time) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                  <Input
                    type="time"
                    value={toTimePart(data.start_date)}
                    onChange={(e) => {
                      const date = toDatePart(data.start_date)
                      const newTime = e.target.value
                      onChange({ start_date: combineDateTime(date, newTime) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                </div>
                {err.start_date && (
                  <p className="text-destructive flex items-center gap-1 text-sm">
                    <div className="bg-status-error h-1 w-1 rounded-full"></div>
                    {err.start_date}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-muted-foreground text-sm font-medium">
                  Fecha y hora de fin
                </Label>
                <div className="space-y-3">
                  <Input
                    type="date"
                    value={toDatePart(data.end_date)}
                    onChange={(e) => {
                      const newDate = e.target.value
                      const time = toTimePart(data.end_date)
                      onChange({ end_date: combineDateTime(newDate, time) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                  <Input
                    type="time"
                    value={toTimePart(data.end_date)}
                    onChange={(e) => {
                      const date = toDatePart(data.end_date)
                      const newTime = e.target.value
                      onChange({ end_date: combineDateTime(date, newTime) })
                    }}
                    required
                    className="border-border focus:border-primary h-12 border-2"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <MapPin className="text-primary h-5 w-5" />
                Ubicación y Participantes
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Barrio
                </Label>
                <Select
                  value={data.neighborhood_id}
                  onValueChange={(value) =>
                    onChange({ neighborhood_id: value })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona un barrio" />
                  </SelectTrigger>
                  <SelectContent>
                    {neighborhoods.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Máx. participantes
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={data.max_participants}
                  onChange={(e) =>
                    onChange({ max_participants: e.target.value })
                  }
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="100"
                />
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Cantidad de ganadores
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={data.winner_count}
                  onChange={(e) => onChange({ winner_count: e.target.value })}
                  required
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="1"
                />
                {err.winner_count && (
                  <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
                    <div className="bg-status-error h-1 w-1 rounded-full"></div>
                    {err.winner_count}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Tag className="text-secondary h-5 w-5" />
                Términos y Condiciones
              </h3>
            </div>

            <div>
              <RichTextEditor
                value={data.terms_and_conditions}
                onChange={(html) => onChange({ terms_and_conditions: html })}
                placeholder="Especifica las reglas y condiciones del sorteo..."
                className="text-base"
                minHeight="160px"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <Users className="text-secondary h-5 w-5" />
                Requisitos de Participación
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Puntos mínimos
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={data.requirements.min_points ?? ""}
                  onChange={(e) =>
                    onChange({
                      requirements: {
                        ...data.requirements,
                        min_points: e.target.value,
                      },
                    })
                  }
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="0"
                />
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Eventos asistidos mínimos
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={data.requirements.min_events_attended ?? ""}
                  onChange={(e) =>
                    onChange({
                      requirements: {
                        ...data.requirements,
                        min_events_attended: e.target.value,
                      },
                    })
                  }
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="0"
                />
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Posts de foro mínimos
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={data.requirements.min_forum_posts ?? ""}
                  onChange={(e) =>
                    onChange({
                      requirements: {
                        ...data.requirements,
                        min_forum_posts: e.target.value,
                      },
                    })
                  }
                  className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                  placeholder="0"
                />
              </div>

              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Barrio requerido
                </Label>
                <Select
                  value={data.requirements.required_neighborhood ?? ""}
                  onValueChange={(value) =>
                    onChange({
                      requirements: {
                        ...data.requirements,
                        required_neighborhood: value,
                      },
                    })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                    <SelectValue placeholder="Selecciona un barrio (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {neighborhoods.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(data.create_viral_campaign)}
              onChange={(e) =>
                onChange({ create_viral_campaign: e.target.checked })
              }
              className="accent-primary h-4 w-4"
            />
            <Label htmlFor="create_viral_campaign" className="text-sm">
              Crear campaña viral asociada a este sorteo
            </Label>
          </div>

          {data.create_viral_campaign && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
              <div>
                <Label className="text-muted-foreground mb-1 block text-xs">
                  Entradas base
                </Label>
                <Input
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                  type="number"
                  value={data.viral_campaign?.base_entries ?? ""}
                  onChange={(e) =>
                    onChange({
                      viral_campaign: {
                        ...data.viral_campaign,
                        base_entries:
                          e.target.value === "" ? "" : Number(e.target.value),
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-1 block text-xs">
                  Entradas por referido
                </Label>
                <Input
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                  type="number"
                  value={data.viral_campaign?.entries_per_referral ?? ""}
                  onChange={(e) =>
                    onChange({
                      viral_campaign: {
                        ...data.viral_campaign,
                        entries_per_referral:
                          e.target.value === "" ? "" : Number(e.target.value),
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-1 block text-xs">
                  Máx. entradas por usuario
                </Label>
                <Input
                  className="border-border focus:border-primary h-12 w-full border-2 text-base transition-colors duration-200"
                  type="number"
                  value={data.viral_campaign?.max_entries_per_user ?? ""}
                  onChange={(e) =>
                    onChange({
                      viral_campaign: {
                        ...data.viral_campaign,
                        max_entries_per_user:
                          e.target.value === "" ? "" : Number(e.target.value),
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-muted-foreground mb-1 block text-xs">
                  Estado
                </Label>
                <Select
                  value={(data.viral_campaign?.status ?? "") as string}
                  onValueChange={(value) =>
                    onChange({
                      viral_campaign: {
                        ...data.viral_campaign,
                        status: value as ViralCampaignFormData["status"],
                      },
                    })
                  }
                >
                  <SelectTrigger className="border-border focus:border-primary h-12 w-full border-2 px-3 py-5">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="draft">draft</SelectItem>
                    <SelectItem value="ended">ended</SelectItem>
                    <SelectItem value="drawn">drawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {processing ? "Guardando..." : submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
